// The second factor at sign-in (decision 20, QA-093): after the first factor, when the person has
// two-factor on. An app code, a text code to their verified mobile, or a recovery code; and
// "Remember this device for 30 days". The words are the sign-in prototype's (site/prototypes/sign-in);
// the card's brand and banner stay the sign-in page's, the title is the step's.

import type { TwoFactorMethod } from '@flowology/client';
import { TWO_FACTOR_LABEL } from './signin-model.ts';
import { useSecondStep, type SignedIn } from '../login-model.ts';
import './signin.css';

// Riverstone's; Blueprint draws the step in SignInPage.tsx, from the same login-model.ts.
export function TwoFactorStep({
  methods,
  tenant,
  company,
  onSignedIn,
  onCancel,
}: {
  methods: readonly TwoFactorMethod[];
  /** Whose sign-in this is, under the title. */
  company?: string;
  /** Named off a company host (local development); on one, the host says. */
  tenant?: string;
  onSignedIn: SignedIn;
  onCancel: () => void;
}) {
  const t = useSecondStep({ methods, tenant, onSignedIn });
  const { method, label, code, setCode, remember, setRemember, texted, error, busy } = t;
  return (
    <form className="login-form" onSubmit={(e) => void t.submit(e)} aria-label="Second step">
      <h1>{label.title}</h1>
      {company && <p className="lede login-lede">to {company}</p>}
      <p className="lede">{t.lede}</p>
      {error && (
        <p className="banner bad login-banner" role="alert">
          {error}
        </p>
      )}
      {method === 'sms' && !texted ? (
        <button
          type="button"
          className="btn primary wide lg"
          disabled={busy}
          onClick={() => void t.sendText()}
        >
          {busy ? 'Sending…' : 'Text me a code'}
        </button>
      ) : (
        <>
          <label>
            {label.field}
            <input
              className="field"
              inputMode={method === 'recovery' ? 'text' : 'numeric'}
              autoComplete={method === 'recovery' ? 'off' : 'one-time-code'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
          </label>
          <label className="checkrow">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember this device for 30 days
          </label>
          <button className="btn primary wide lg" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </>
      )}
      <div className="login-other" role="group" aria-label="Other ways to confirm it’s you">
        {t.others.map((m) => (
          <button key={m} type="button" className="lnk" onClick={() => t.switchTo(m)}>
            {TWO_FACTOR_LABEL[m].switchTo}
          </button>
        ))}
        <span className="small muted">Lost every way in? Ask the owner to reset your two-factor.</span>
        <button type="button" className="lnk" onClick={onCancel}>
          Start again
        </button>
      </div>
    </form>
  );
}
